export enum SessionStatus {
  PRE_CREATED = "PRE_CREATED",
  PENDING = "PENDING",
  ACTIVE = "ACTIVE",
  EXPIRED = "EXPIRED",
  COMPLETED = "COMPLETED",
  TERMINATED = "TERMINATED"
}

export interface Session {
  id: number;
  session_id: string;
  client_user_id: string;
  client_username?: string;
  client_timezone?: string;
  client_invite_link?: string;
  agent_username?: string;
  agent_user_id?: string;
  agent_group_id?: string;
  agent_invite_link?: string;
  agent_matrix_username?: string;
  matrix_room_id?: string;
  matrix_room_link?: string;
  client_language_id?: number;
  bot_invite_code?: string;
  agent_access_url?: string;
  message_limit: number;
  messages_sent_by_client: number;
  messages_sent_by_agent: number;
  session_lifetime_hours: number;
  limit_reached_notified: boolean;
  status: SessionStatus;
  expires_at: string;
  creator_account_id?: number; // Optional - not used in current implementation
  created_at: string;
  updated_at: string;
}
export interface SessionListResponse {
  sessions: Session[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

export enum MessageType {
  TEXT = "text",
  PHOTO = "photo",
  VIDEO = "video",
  DOCUMENT = "document",
  AUDIO = "audio",
  VOICE = "voice",
  STICKER = "sticker",
  SYSTEM = "system"
}

export enum MessageSender {
  CLIENT = "client",
  AGENT = "agent",
  SYSTEM = "system"
}

export interface Message {
  id: number;
  session_id: number;
  telegram_message_id?: string;
  content: string;
  message_type: MessageType;
  sender_type: MessageSender;
  sender_telegram_id?: string;
  sender_username?: string;
  is_relayed: boolean;
  relay_message_id?: string;
  reply_to_message_id?: number;
  file_id?: string;
  file_unique_id?: string;
  file_size?: number;
  file_name?: string;
  mime_type?: string;
  processed_at?: string;
  processing_error?: string;
  is_flagged: boolean;
  flag_reason?: string;
  created_at: string;
  updated_at: string;
}

export interface SystemStats {
  total_sessions: number;
  active_sessions: number;
  pending_sessions: number;
  completed_sessions: number;
  total_messages: number;
  messages_today: number;
}

export interface MessageListResponse {
  messages: Message[];
  total: number;
  page: number;
  per_page: number;
}

export interface CreateSessionRequest {
  message_limit?: number;
  session_lifetime_hours?: number;
  client_username?: string;
  client_timezone?: string;
  agent_username?: string;
  agent_matrix_username?: string;
  client_language_id?: number;
  agent_language_id?: number;
}

export interface CreateSessionResponse {
  session_id: string;
  client_invite_link: string;
  agent_invite_link?: string;
  matrix_room_link?: string;
  message_limit: number;
}

export interface SessionCreateRequest {
  message_limit?: number;
  session_lifetime_hours?: number;
  client_username?: string;
  client_timezone?: string;
  agent_username?: string;
  agent_matrix_username?: string;
  client_language_id?: number;
  agent_language_id?: number;
}

export interface Language {
  id: number;
  name: string;
  code: string;
  usage: string;
  createdAt: string;
  updatedAt: string | null;
}

export interface ApiResponse<T> {
  data?: T;
  error?: string;
  message?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
  role: string;
  /** reCAPTCHA Enterprise token (score-based). Required by POST /auth/login. */
  captcha: string;
}

export interface LoginResponse {
  token: string;
}

export interface RefreshTokenResponse {
  token: string;
}

export interface User {
  id: number;
  profile_path: string;
  email: string;
  firstName: string;
  lastName: string;
  username: string;
  role: 'agent' | 'admin' | 'user';
  valid: boolean;
  langs: string;
  languageId: number;
  country_id: number;
  timezone: string;
  timeFormat: 'h12' | 'h24';
  mobile_1: string;
  mobile_2: string;
  phone_1: string;
  phone_2: string;
  vonage_id: string;
  whatsapp_notice_accepted: boolean;
  is_public_profile_active: boolean;
}

export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  loading: boolean;
}

export interface Bot {
  id: number;
  bot_username: string | null;
  bot_token: string;
  bot_id: number | null;
  is_active: boolean;
  is_available: boolean;
  max_sessions: number;
  current_sessions: number;
  container_id: string | null;
  container_hostname: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface SystemMessage {
  id: number;
  key: string;
  label: string;
  category: string;
  content: string;
  default_content: string;
  language_code: string;
  has_translation: boolean;
  description: string | null;
  is_active: boolean;
  translation_updated_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}